import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Leftsidebar from "../../sidebar/leftsidebar";
import LeftResponsivesidebar from "../../sidebar/left-responsive";
import Headmenu from "../../sidebar/headmenu";
import availableBalanceIcon from "../../../assets/images/icons/billing/available-balance.svg";
import currentSpendIcon from "../../../assets/images/icons/billing/current-spend.svg";
import BillingBackButton from "./BillingBackButton";
import { getSpendingControls, updateSpendingControls, getSpendSummary } from "../../../hook/useAdsApi";

const ALL_THRESHOLDS = [40, 60, 80, 100];

// Wired to GET/PATCH /ad-account/spending-controls (cap + alert_thresholds) and
// GET /ad-account/spend-summary (current-spend/remaining/percentage). Both throw "Configure
// billing before setting a spending cap" without a profile - the Dashboard's "Spending Controls"
// CTA is already disabled until then, so this only gets reached with a real profile normally.
// Notification Channels (Email/In-App) has no backing field anywhere in the system - kept as a
// UI-only section, never sent to the API, until that concept actually gets built.
export default function AdSpendingControls() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currency, setCurrency] = useState(null);
  const [cap, setCap] = useState("");
  const [alertThresholds, setAlertThresholds] = useState([40, 60, 80, 100]);
  const [channels, setChannels] = useState({ email: true, inApp: true });
  const [currentSpend, setCurrentSpend] = useState(0);
  const [remaining, setRemaining] = useState(0);
  const [percentageUsed, setPercentageUsed] = useState(0);

  const refresh = () =>
    Promise.all([getSpendingControls(), getSpendSummary()])
      .then(([controls, summary]) => {
        if (controls) {
          setCurrency(controls.currency);
          setCap(controls.account_spend_cap ?? "");
          if (controls.alert_thresholds?.length) setAlertThresholds(controls.alert_thresholds);
        }
        if (summary) {
          setCurrentSpend(Number(summary.current_spend || 0));
          setRemaining(summary.remaining !== null ? Number(summary.remaining) : 0);
          setPercentageUsed(summary.percentage_used ?? 0);
        }
      })
      .catch(() => {});

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, []);

  const toggleThreshold = (value) => {
    setAlertThresholds((prev) =>
      prev.includes(value) ? prev.filter((t) => t !== value) : [...prev, value].sort((a, b) => a - b)
    );
  };

  const toggleChannel = (key) => setChannels((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleSave = async () => {
    if (!cap || Number(cap) <= 0) {
      toast.error("Enter a spending cap greater than zero");
      return;
    }
    setSaving(true);
    try {
      await updateSpendingControls({
        account_spend_cap: Number(cap),
        alert_thresholds: alertThresholds,
      });
      toast.success("Spending controls saved");
      // Re-fetch spend-summary too, not just the controls response - the cap changing shifts
      // "Remaining" and the progress bar, which updateSpendingControls' own response doesn't
      // carry (that's spend-summary's shape). Without this the top panel looked "not wired"
      // since it kept showing whatever was loaded on page mount.
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not save spending controls, try again");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard w-full min-h-screen flex">
        <Leftsidebar />
        <main className="flex-1 p-6 pl-8">
          <LeftResponsivesidebar />
          <Headmenu />
          <p className="text-sm text-gray-500">Loading spending controls...</p>
        </main>
      </div>
    );
  }

  return (
    <div className="dashboard w-full min-h-screen flex">
      <Leftsidebar />
      <main className="flex-1 p-6 pl-8">
        <LeftResponsivesidebar />
        <Headmenu />
        <main className="flex">
          <div className="w-full max-w-3xl">
            <div className="mb-6 flex items-start justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Spending Controls</h1>
                <p className="text-sm text-gray-500 mt-1">
                  Set your account spending cap and get notified to stay in control.
                </p>
              </div>
              <BillingBackButton />
            </div>

            <div className="rounded-2xl bg-[#fdfae6] p-6 mb-5">
              <div className="flex items-center gap-8 mb-5">
                <div className="flex items-center gap-3">
                  <img src={availableBalanceIcon} alt="" className="w-12 h-12" />
                  <div>
                    <p className="text-2xl font-black">$ {currentSpend}</p>
                    <p className="text-sm text-gray-600">Current Spend</p>
                  </div>
                </div>
                <div className="w-px h-12 bg-gray-300" />
                <div className="flex items-center gap-3">
                  <img src={currentSpendIcon} alt="" className="w-12 h-12" />
                  <div>
                    <p className="text-2xl font-black">$ {remaining}</p>
                    <p className="text-sm text-gray-600">Remaining</p>
                  </div>
                </div>
              </div>

              <p className="text-sm font-semibold text-gray-700 mb-2">Amount Spend for this Month</p>
              <div className="flex items-center gap-3">
                <div className="flex-1 h-3 bg-white rounded-full overflow-hidden">
                  <div className="h-full bg-[#095228]" style={{ width: `${percentageUsed}%` }} />
                </div>
                <span className="text-sm font-semibold whitespace-nowrap">{percentageUsed} %</span>
              </div>
            </div>

            <div className="rounded-2xl border border-gray-200 p-5 mb-5">
              <h3 className="font-bold text-base mb-1">Account Spending Cap</h3>
              <p className="text-sm text-gray-500 mb-3">Set a maximum limit for your total advertising spend</p>
              {/* Currency isn't selectable here - the cap always uses the profile's configured
                  billing currency (set via Billing Model / Tax & Billing Info), shown read-only. */}
              <div className="flex border border-gray-300 rounded-lg overflow-hidden max-w-sm">
                <span className="px-3 py-2 text-sm border-r border-gray-300 bg-gray-50 text-gray-600 font-semibold">
                  {currency || "$"}
                </span>
                <input
                  type="number"
                  min="0"
                  value={cap}
                  onChange={(e) => setCap(e.target.value)}
                  placeholder="0"
                  className="flex-1 px-3 py-2 text-sm outline-none"
                />
              </div>
              <p className="text-xs text-gray-400 mt-1">
                Does not reset automatically - raise it manually when you need more.
              </p>
            </div>

            <div className="rounded-2xl bg-gray-50 p-5 mb-5">
              <h3 className="font-bold text-base mb-1">Spend Alerts</h3>
              <p className="text-sm text-gray-500 mb-3">
                Get notified when your advertising spend reaches the selected thresholds
              </p>
              <div className="flex gap-3 flex-wrap">
                {ALL_THRESHOLDS.map((t) => (
                  <label
                    key={t}
                    className="flex items-center gap-2 text-base font-semibold rounded-lg bg-[#e9f5ec] px-4 py-2.5 cursor-pointer"
                  >
                    <span
                      className={`w-5 h-5 rounded flex items-center justify-center text-white text-xs ${
                        alertThresholds.includes(t) ? "bg-[#095228]" : "bg-gray-300"
                      }`}
                      onClick={() => toggleThreshold(t)}
                    >
                      {alertThresholds.includes(t) ? "✓" : ""}
                    </span>
                    {t}%
                  </label>
                ))}
              </div>
            </div>

            <div className="rounded-2xl bg-gray-50 p-5 mb-6">
              <h3 className="font-bold text-base mb-1">Notification Channels</h3>
              <p className="text-sm text-gray-500 mb-3">Choose how you want to receive alerts</p>
              {/* UI-only, not wired to any API - there's no channel-preference concept anywhere
                  in the notification system today (createNotification sends unconditionally). */}
              <div className="grid grid-cols-2 gap-3">
                <label className="flex items-start gap-2 rounded-lg bg-[#fdfae6] px-4 py-3 cursor-pointer">
                  <span
                    className={`w-5 h-5 rounded flex items-center justify-center text-white text-xs mt-0.5 ${
                      channels.email ? "bg-[#095228]" : "bg-gray-300"
                    }`}
                    onClick={() => toggleChannel("email")}
                  >
                    {channels.email ? "✓" : ""}
                  </span>
                  <div>
                    <p className="font-bold text-sm">E Mail</p>
                    <p className="text-xs text-gray-500">Get alerts via Email</p>
                  </div>
                </label>
                <label className="flex items-start gap-2 rounded-lg bg-[#fdfae6] px-4 py-3 cursor-pointer">
                  <span
                    className={`w-5 h-5 rounded flex items-center justify-center text-white text-xs mt-0.5 ${
                      channels.inApp ? "bg-[#095228]" : "bg-gray-300"
                    }`}
                    onClick={() => toggleChannel("inApp")}
                  >
                    {channels.inApp ? "✓" : ""}
                  </span>
                  <div>
                    <p className="font-bold text-sm">In-App</p>
                    <p className="text-xs text-gray-500">Receive alerts in your app</p>
                  </div>
                </label>
              </div>
            </div>

            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="bg-[#095228] text-white font-semibold px-6 py-2.5 rounded-full text-sm hover:opacity-90 transition disabled:opacity-60"
            >
              {saving ? "Saving..." : "Save Spending Controls"}
            </button>
          </div>
        </main>
      </main>
    </div>
  );
}
